import React, { useState, useEffect } from 'react';
import styles from '../../assets/scss/InventoryManagement/InventoryTable.module.scss';
import settingStyles from '../../assets/scss/Settings/Settings.module.scss';
import { FaHeadset } from 'react-icons/fa';
import Pagination from '../../Components/Common/Pagination';
import { useGetDashboardQueriesQuery, useUpdateDashboardQueryStatusMutation } from '../../slices/admin/admin';
import { toast } from 'react-toastify';

const STATUS_CONFIG = {
    received: { label: 'Pending', color: '#e53935', bg: '#fdecea' },
    followup: { label: 'Follow Up', color: '#f9a825', bg: '#fffde7' },
    completed: { label: 'Completed', color: '#2e7d32', bg: '#e8f5e9' },
};

const UserQuery = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState('');

    // API Hooks
    const { data: queryData, isLoading, isFetching } = useGetDashboardQueriesQuery({
        offset: currentPage,
        limit: limit,
        search: search
    });

    const [updateStatus] = useUpdateDashboardQueryStatusMutation();

    const enquiries = queryData?.result || [];
    const totalCount = queryData?.total || 0;
    const totalPages = Math.ceil(totalCount / limit);

    const handleStatusChange = async (id, newStatus) => {
        try {
            await updateStatus({ id, status: newStatus }).unwrap();
            toast.success("Status updated successfully");
        } catch (error) {
            toast.error(error?.data?.message || "Failed to update status");
        }
    };

    return (
        <div className={styles.inventoryContainer}>
            <div className={settingStyles.headerRow}>
                <h2 className={settingStyles.header} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaHeadset />
                    Enquiry
                </h2>
            </div>

            {/* Search below header */}
            <div className={styles.filterRow} style={{ marginBottom: '12px' }}>
                <input
                    type="text"
                    placeholder="Search by Name / Phone..."
                    className={styles.searchInput}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                />
            </div>

            {/* Table */}
            <div className={styles.tableWrapper}>
                <div className={styles.tableScroll}>
                    <table style={{ opacity: isFetching ? 0.6 : 1 }}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Request From</th>
                                <th>Phone Number</th>
                                <th>Email</th>
                                <th>Message</th>
                                <th>Date</th>
                                <th>Status Updated</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan="8" className={styles.noData}>Loading enquiries...</td></tr>
                            ) : enquiries.length === 0 ? (
                                <tr><td colSpan="8" className={styles.noData}>No enquiries found</td></tr>
                            ) : (
                                enquiries.map((item, idx) => {
                                    const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.received;
                                    return (
                                        <tr key={item._id}>
                                            <td>{(currentPage - 1) * limit + idx + 1}</td>
                                            <td style={{ fontWeight: 500 }}>{item.name || '-'}</td>
                                            <td className="notranslate">{item.phone || '-'}</td>
                                            <td>{item.email || '-'}</td>
                                            <td style={{ maxWidth: '250px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {item.query || '-'}
                                            </td>
                                            <td>
                                                <div style={{ fontSize: '12px' }}>
                                                    {new Date(item.createdAt).toLocaleDateString('en-IN')}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#888' }}>
                                                    {new Date(item.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    <span
                                                        style={{
                                                            display: 'inline-block',
                                                            padding: '2px 8px',
                                                            borderRadius: '12px',
                                                            fontSize: '12px',
                                                            fontWeight: 600,
                                                            color: cfg.color,
                                                            background: cfg.bg,
                                                            whiteSpace: 'nowrap',
                                                            width: 'fit-content'
                                                        }}
                                                    >
                                                        {cfg.label}
                                                    </span>
                                                    {item.updatedBy && (
                                                        <>
                                                            <span style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                                                                By {item.updatedBy.firstName} {item.updatedBy.lastName}
                                                            </span>
                                                            <span style={{ fontSize: '11px', color: '#aaa' }}>
                                                                {new Date(item.updatedAt).toLocaleDateString('en-IN')} {new Date(item.updatedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            {/* Action dropdown */}
                                            <td>
                                                <select
                                                    className="notranslate"
                                                    translate="no"
                                                    value={item.status}
                                                    onChange={(e) => handleStatusChange(item._id, e.target.value)}
                                                    style={{
                                                        padding: '6px 10px',
                                                        fontSize: '13px',
                                                        fontWeight: 600,
                                                        borderRadius: '6px',
                                                        border: '1px solid #ccc',
                                                        background: '#fff',
                                                        color: STATUS_CONFIG[item.status]?.color || '#333',
                                                        cursor: 'pointer',
                                                        outline: 'none',
                                                        minWidth: '120px',
                                                    }}
                                                >
                                                    <option className="notranslate" translate="no" value="received" style={{ color: '#e53935', fontWeight: 600 }}>Pending</option>
                                                    <option className="notranslate" translate="no" value="followup" style={{ color: '#f9a825', fontWeight: 600 }}>Follow Up</option>
                                                    <option className="notranslate" translate="no" value="completed" style={{ color: '#2e7d32', fontWeight: 600 }}>Completed</option>
                                                </select>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className={styles.paginationWrapper}>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page)}
                />
                <select
                    className={styles.inputsbtn}
                    value={limit}
                    onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }}
                >
                    <option value={10}>10 rows</option>
                    <option value={20}>20 rows</option>
                    <option value={50}>50 rows</option>
                </select>
            </div>
        </div>
    );
};

export default UserQuery;
